/**
 * News article editor — same shape as Pages editor, plus excerpt + cover
 * image + category + tags. Uses MediaPicker for both cover and inline images
 * inside the TipTap body.
 */
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Eye, EyeOff, ImagePlus, Save, X as XIcon } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";

import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { TipTapEditor } from "@/components/editor/TipTapEditor";
import { MediaPicker } from "@/components/editor/MediaPicker";
import { StatusBadge } from "@/components/StatusBadge";
import { PageHeader } from "@/components/PageHeader";
import { useAutosave, formatSavedAt } from "@/hooks/useAutosave";
import { slugify } from "@/hooks/useSlug";

export const Route = createFileRoute("/_app/content/news/$id")({ component: NewsEditor });

interface NewsDetail {
  id: number;
  slug: string;
  title: string;
  excerpt: string | null;
  body_md: string | null;
  cover_r2_key: string | null;
  category: string | null;
  tags_csv: string | null;
  status: "draft" | "published" | "archived";
  published_at: number | null;
  updated_at: number;
}

const schema = z.object({
  title: z.string().min(1).max(200),
  slug: z.string().min(1).max(120).regex(/^[a-z0-9-]+$/),
  excerpt: z.string().max(500).nullable(),
  body_md: z.string().max(500_000).nullable(),
  cover_r2_key: z.string().nullable(),
  category: z.string().max(60).nullable(),
  tags_csv: z.string().max(300).nullable(),
});
type FormValues = z.infer<typeof schema>;

const apiBase = (import.meta.env.VITE_API_URL as string | undefined) ?? "/api";
const r2Url = (k: string) => `${apiBase}/admin/media/file/${k}`;

function NewsEditor() {
  const { id } = Route.useParams();
  const { can } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [coverPickerOpen, setCoverPickerOpen] = useState(false);

  const { data: row, isLoading } = useQuery({
    queryKey: ["news", "detail", id],
    queryFn: () => api.get<NewsDetail>(`/admin/news/${id}`),
  });

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    values: row ? {
      title: row.title, slug: row.slug, excerpt: row.excerpt, body_md: row.body_md,
      cover_r2_key: row.cover_r2_key, category: row.category, tags_csv: row.tags_csv,
    } : undefined,
  });
  const { register, handleSubmit, watch, setValue, control, formState: { errors, isDirty } } = form;

  const update = useMutation({
    mutationFn: (v: Partial<FormValues>) => api.patch(`/admin/news/${id}`, v),
    // List only — refetching the detail would feed the server row back through
    // useForm's `values` and reset the form mid-edit. See events editor.
    onSuccess: () => qc.invalidateQueries({ queryKey: ["news", "list"] }),
  });
  const publish = useMutation({
    mutationFn: () => api.post(`/admin/news/${id}/publish`),
    onSuccess: () => { toast.success("Published"); qc.invalidateQueries({ queryKey: ["news"] }); },
  });
  const unpublish = useMutation({
    mutationFn: () => api.post(`/admin/news/${id}/unpublish`),
    onSuccess: () => { toast.success("Unpublished"); qc.invalidateQueries({ queryKey: ["news"] }); },
  });

  const formValues = watch();
  const { status: saveStatus, savedAt } = useAutosave({
    value: formValues,
    enabled: !!row && form.formState.isValid && isDirty,
    delayMs: 1500,
    onSave: (v) => update.mutateAsync(v),
  });

  const slugTouched = useRef(false);
  const lastTitle = useRef("");
  useEffect(() => {
    const t = formValues.title;
    if (!t || slugTouched.current) return;
    if (t === lastTitle.current) return;
    lastTitle.current = t;
    setValue("slug", slugify(t));
  }, [formValues.title, setValue]);

  if (isLoading || !row) return <div className="text-sm text-muted-foreground">Loading…</div>;

  return (
    <div className="space-y-4">
      <PageHeader
        title={row.title || "Untitled"}
        back={{ href: "/content/news", label: "News" }}
        actions={
          <>
            <span className="text-xs text-muted-foreground">
              {saveStatus === "saving" && "Saving…"}
              {saveStatus === "saved" && `Saved ${formatSavedAt(savedAt)}`}
              {saveStatus === "dirty" && "Unsaved changes"}
              {saveStatus === "error" && <span className="text-destructive">Save failed</span>}
            </span>
            <Button variant="outline" size="sm" disabled={!can("news", "update")} onClick={handleSubmit((v) => update.mutateAsync(v).then(() => toast.success("Saved")))}>
              <Save className="h-4 w-4" /> Save
            </Button>
            {row.status === "published" ? (
              <Button variant="secondary" size="sm" disabled={!can("news", "publish")} onClick={() => unpublish.mutate()}>
                <EyeOff className="h-4 w-4" /> Unpublish
              </Button>
            ) : (
              <Button size="sm" disabled={!can("news", "publish")} onClick={() => publish.mutate()}>
                <Eye className="h-4 w-4" /> Publish
              </Button>
            )}
          </>
        }
      />

      <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
        <form className="space-y-4" onSubmit={(e) => e.preventDefault()}>
          <Card>
            <CardContent className="space-y-4 pt-6">
              <Input className="h-12 border-0 px-0 text-3xl font-light tracking-tight focus-visible:ring-0" placeholder="Article title" {...register("title")} />
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <span>/news/</span>
                <Input
                  className="h-7 max-w-[300px] border-0 bg-secondary px-2 font-mono text-xs"
                  {...register("slug", { onChange: () => { slugTouched.current = true; } })}
                />
                {errors.slug && <span className="text-destructive text-xs">{errors.slug.message}</span>}
              </div>
              <Textarea
                rows={2}
                placeholder="One-line summary that appears in news lists and OG previews…"
                {...register("excerpt")}
                className="text-base"
              />
            </CardContent>
          </Card>

          <Card>
            <CardContent className="px-0 pt-0 pb-0">
              <Controller
                control={control}
                name="body_md"
                render={({ field }) => (
                  <TipTapEditor value={field.value ?? ""} onChange={field.onChange} surface="article" className="border-0 shadow-none" />
                )}
              />
            </CardContent>
          </Card>
        </form>

        <aside className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Status</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Current</span>
                <StatusBadge status={row.status} />
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Published</span>
                <span>{row.published_at ? format(row.published_at * 1000, "MMM d, yyyy") : "—"}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Updated</span>
                <span>{format(row.updated_at * 1000, "MMM d, yyyy")}</span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Cover image</CardTitle>
            </CardHeader>
            <CardContent>
              {watch("cover_r2_key") ? (
                <div className="space-y-2">
                  <img src={r2Url(watch("cover_r2_key")!)} alt="" className="aspect-video w-full rounded-md border object-cover" />
                  <div className="flex gap-2">
                    <Button type="button" variant="outline" size="sm" onClick={() => setCoverPickerOpen(true)}>
                      <ImagePlus className="h-4 w-4" /> Replace
                    </Button>
                    <Button type="button" variant="ghost" size="sm" onClick={() => setValue("cover_r2_key", null, { shouldDirty: true })}>
                      <XIcon className="h-4 w-4" /> Remove
                    </Button>
                  </div>
                </div>
              ) : (
                <Button type="button" variant="outline" size="sm" className="w-full" onClick={() => setCoverPickerOpen(true)}>
                  <ImagePlus className="h-4 w-4" /> Choose image
                </Button>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Taxonomy</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="space-y-1.5">
                <Label htmlFor="category" className="text-xs">Category</Label>
                <Input id="category" placeholder="Placement, Research…" {...register("category")} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="tags_csv" className="text-xs">Tags</Label>
                <Input id="tags_csv" placeholder="zoho, hackathon, 2026" {...register("tags_csv")} />
                <p className="text-xs text-muted-foreground">Comma-separated.</p>
              </div>
              <Separator />
              <Link to="/content/news" as never className="block text-xs text-muted-foreground hover:text-primary">← All news</Link>
            </CardContent>
          </Card>
        </aside>
      </div>

      <MediaPicker
        open={coverPickerOpen}
        onOpenChange={setCoverPickerOpen}
        onPick={(url) => {
          const key = url.split("/admin/media/file/")[1] ?? "";
          setValue("cover_r2_key", key, { shouldDirty: true });
          setCoverPickerOpen(false);
        }}
      />
    </div>
  );
}
